import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";

import { resources, locales, type Locale } from "./locales";

export const LANGUAGE_STORAGE_KEY = "cyprus-trees.language";
const FALLBACK: Locale = "en";

function isLocale(value: string | null | undefined): value is Locale {
  return typeof value === "string" && (locales as string[]).includes(value);
}

/** URL choice, stored choice, first matching browser language, then English. */
export function detectLanguage(): Locale {
  const requested = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(requested)) return requested;

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage can be unavailable (private browsing); fall through to the browser's languages.
  }

  for (const tag of window.navigator.languages ?? []) {
    const base = tag.split("-")[0];
    if (isLocale(base)) return base;
  }

  return FALLBACK;
}

const i18n = createInstance();

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: FALLBACK,
  supportedLngs: [...locales],
  defaultNS: "translation",
  ns: ["translation"],
  returnNull: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

i18n.on("languageChanged", (language) => {
  document.documentElement.lang = language;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // A visitor who blocks storage still gets the switch for this session.
  }
});

document.documentElement.lang = i18n.language;

export default i18n;
export type { Locale };
